'use client';

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import {
  Avatar,
  Card,
  FilterBar,
  Link,
  Numeric,
  SearchInput,
  type FilterGroup,
  type FilterValue,
  selectValue,
} from '@/components/ui';
import { EventFollowStatus } from './EventFollowStatus';
import { useEventFollows } from './EventFollowProvider';
import { useMatchFilterUrl } from './useMatchFilterUrl';
import { cn } from '@/lib/utils';
import {
  eventPlayerHref,
  NO_PLAYER_FILTER,
  filterEventPlayers,
  hasPlayerFilter,
  hasTeamRows,
  playerFacets,
  playerFacetCounts,
  playerPlaceText,
  type EventPlayer,
  type PlayerFilter,
} from '@/app/[locale]/event/[eventId]/libs/eventPlayers';
import { flagEmoji } from '@/app/[locale]/event/[eventId]/libs/playerCountry';

/** The one line under a row's title. */
function RowLine({ p }: { p: EventPlayer }) {
  if (p.members.length > 0) return <>{p.members.join(' · ')}</>;
  const place = playerPlaceText(p);
  if (place && p.country) {
    return (
      <>
        <span aria-hidden>{flagEmoji(p.country.code)} </span>
        {place}
      </>
    );
  }
  return <>{p.divisions.map((d) => d.name).join(' · ')}</>;
}

/**
 * The Players tab: everyone entered, A–Z (sorted on the server), with search
 * and Country / State / Division filters (`FilterBar`). A team entry is one row, its team name
 * over its player names. The component receives only what the server let this
 * viewer see and never decides visibility itself. A filter appears only when it
 * offers a real choice (two or more values).
 */
export function EventPlayersList({ players }: { players: EventPlayer[] }) {
  const t = useTranslations('Event.players');
  const { locale, eventId, slug } = useParams() as {
    locale: string;
    eventId: string;
    slug: string;
  };
  const follows = useEventFollows();
  const tFollow = useTranslations('Event.follow');
  const { state: url, update: updateUrl } = useMatchFilterUrl();
  const [filter, setFilter] = useState<PlayerFilter>(NO_PLAYER_FILTER);
  const effectiveFilter = { ...filter, division: url.division };
  const available = useMemo(
    () => (url.following ? players.filter((p) => follows.playerIds.has(p.playerId)) : players),
    [players, url.following, follows.playerIds],
  );
  const facets = useMemo(() => playerFacets(players), [players]);
  const counts = useMemo(
    () => playerFacetCounts(available, effectiveFilter),
    [available, effectiveFilter],
  );
  const shown = useMemo(
    () => filterEventPlayers(available, effectiveFilter),
    [available, effectiveFilter],
  );
  const filtered = hasPlayerFilter(effectiveFilter) || url.following;
  const update = (patch: Partial<PlayerFilter>) => setFilter((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (url.division && !facets.divisions.some((d) => d.id === url.division))
      updateUrl({ division: '' }, true);
  }, [url.division, facets.divisions, updateUrl]);

  useEffect(() => {
    setFilter((previous) => {
      const next = { ...previous };
      if (previous.country && !facets.countries.some((c) => c.code === previous.country))
        next.country = '';
      if (previous.state && !facets.states.some((s) => s.value === previous.state)) next.state = '';
      if (previous.division && !facets.divisions.some((d) => d.id === previous.division))
        next.division = '';
      return next.country !== previous.country ||
        next.state !== previous.state ||
        next.division !== previous.division
        ? next
        : previous;
    });
  }, [facets]);

  if (players.length === 0) {
    return (
      <Card className="min-w-0 p-4 md:p-6 print:border-0 print:bg-transparent print:p-0 print:shadow-none">
        <p className="text-muted py-8 text-center text-sm">{t('empty')}</p>
      </Card>
    );
  }

  const groups: FilterGroup[] = [
    {
      kind: 'select',
      id: 'following',
      label: tFollow('players'),
      allLabel: tFollow('allPlayers'),
      showWhenSingle: true,
      options: [{ value: '1', label: tFollow('following') }],
    },
    {
      kind: 'select',
      id: 'country',
      label: t('country'),
      allLabel: t('allCountries'),
      allCount: counts.country.all,
      options: facets.countries.map((c) => ({
        value: c.code,
        label: `${flagEmoji(c.code)} ${c.iso3}`,
        count: counts.country.options[c.code] ?? 0,
      })),
    },
    {
      kind: 'select',
      id: 'state',
      label: t('state'),
      allLabel: t('allStates'),
      allCount: counts.state.all,
      options: facets.states.map((s) => ({
        value: s.value,
        label: s.value,
        count: counts.state.options[s.value] ?? 0,
      })),
    },
    {
      kind: 'select',
      id: 'division',
      label: t('division'),
      allLabel: t('allDivisions'),
      allCount: counts.division.all,
      options: facets.divisions.map((d) => ({
        value: d.id,
        label: d.name,
        count: counts.division.options[d.id] ?? 0,
      })),
    },
  ];
  const values: FilterValue = {
    following: url.following ? '1' : undefined,
    country: filter.country || undefined,
    state: filter.state || undefined,
    division: url.division || undefined,
  };
  // A group reset (chip x, Reset) keeps the search text; Clear all clears it via onClearSearch.
  const onFilterChange = (next: FilterValue) => {
    updateUrl({
      division: selectValue(next, 'division') ?? '',
      following: selectValue(next, 'following') === '1',
    });
    setFilter((f) => ({
      ...f,
      country: selectValue(next, 'country') ?? '',
      state: selectValue(next, 'state') ?? '',
      division: selectValue(next, 'division') ?? '',
    }));
  };

  return (
    <section aria-labelledby="event-players-heading" className="min-w-0">
      <FilterBar
        presentation="sidebar"
        groups={groups}
        value={values}
        onChange={onFilterChange}
        resultCount={shown.length}
        searchActive={filter.query.trim() !== '' || url.following}
        onClearSearch={() => {
          update({ query: '' });
          updateUrl({ division: '', following: false });
        }}
        labels={{
          filters: t('filters'),
          filtersApplied: (count) => t('filtersApplied', { count }),
          reset: t('reset'),
          clearAll: t('clearAll'),
          show: (count) => t('show', { count }),
          remove: (label) => t('remove', { label }),
        }}
        search={
          <div className="space-y-2">
            <EventFollowStatus following={url.following} />
            <SearchInput
              value={filter.query}
              onChange={(query) => update({ query })}
              placeholder={t('search')}
              aria-label={t('search')}
              className="h-11"
            />
          </div>
        }
      >
        <Card className="min-w-0 p-4 md:p-6 print:border-0 print:bg-transparent print:p-0 print:shadow-none">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3">
            <h2
              id="event-players-heading"
              className="text-foreground text-md font-semibold"
              aria-live="polite"
            >
              {filtered
                ? t('showing', { shown: shown.length, total: players.length })
                : t(hasTeamRows(players) ? 'entryCount' : 'count', { count: players.length })}
            </h2>
            <span className="text-muted text-xs">{t('sorted')}</span>
          </div>

          {url.following &&
          (!follows.ready || follows.error || !follows.userId) ? null : shown.length === 0 ? (
            <p className="text-muted py-8 text-center text-sm">
              {url.following && follows.playerIds.size === 0 ? t('followingEmpty') : t('noMatch')}
            </p>
          ) : (
            <ul className="divide-border mt-2 divide-y">
              {shown.map((p) => {
                const body = (
                  <>
                    <Avatar name={p.label} photoUrl={p.photo} />
                    <span className="min-w-0 flex-1">
                      <span
                        data-player-name
                        className="text-foreground block text-sm font-medium break-words"
                      >
                        {p.label}
                      </span>
                      <span className="text-muted block text-xs break-words">
                        <RowLine p={p} />
                      </span>
                    </span>
                    {p.rating !== null && (
                      <Numeric className="text-foreground shrink-0 text-sm">
                        <span className="sr-only">{t('rating')} </span>
                        {p.rating}
                      </Numeric>
                    )}
                  </>
                );
                const row = 'flex min-h-11 items-center gap-3 py-2';
                return (
                  <li key={p.id} className="flex items-center gap-2">
                    {
                      <Link
                        href={`/${locale}${eventPlayerHref(eventId, slug, p.playerId, url.division || p.divisions[0]?.id)}`}
                        className={cn(row, 'hover:bg-surface min-w-0 flex-1')}
                      >
                        {body}
                      </Link>
                    }
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </FilterBar>
    </section>
  );
}
